import React from 'react';
import ReactDOM from 'react-dom';
import debounce from 'lodash/debounce';

import noop from 'utils/noop';
import {
  EditorEvent,
  GraphNodeEvent,
  InlineEditState,
  ItemState,
  VisibilityState,
} from '../../constants';
import { InlineEditStateEvent, GraphEvent, Item, ItemData } from '../../types';
import { isGraphEditable, updateGlobal, executeBatch, normalizeItemData } from '../../utils';
import { EditorContextProps, withEditorContext } from '../../hooks';
import { getStyles, updateShapeVisibility } from './InlineEdit.utils';
import { InlineEditCharacterCount, InlineEditWrapper, InlineEditInput } from './InlineEdit.styles';

export const INLINE_EDIT_INPUT_TEST_ID = 'inline-edit-input';
export const INLINE_EDIT_WRAPPER_TEST_ID = 'inline-edit-wrapper';
export const INLINE_EDIT_CHARACTERCOUNT_TEST_ID = 'inline-edit-charactercount';

interface InlineEditProps extends EditorContextProps {
  /**
   * Property being edited
   * Currently only supports strings
   */
  property?: string;
  /** The class name of the text shape associated with the property */
  shapeClassName?: string;
  /**
   * Should the item value automatically be updated through flowchart?
   * **Recommended**: Keep as false and set store value with `onUpdate`
   */
  autoUpdate?: boolean;
  /** Additional props for the input component */
  inputProps?: React.InputHTMLAttributes<HTMLTextAreaElement>;
  /** Show a character count */
  isCharacterCountShown?: boolean;
  /** Should editing start? */
  shouldBegin?: (e: Partial<InlineEditStateEvent> | Partial<GraphEvent>) => boolean;
  /** Should editing end? */
  shouldEnd?: (
    e:
      | InlineEditStateEvent
      | (React.FocusEvent<HTMLTextAreaElement> & { item: GraphEvent['item'] }),
  ) => boolean;
  /** Callback when inline edit begins  */
  onBegin?: (data?: ItemData) => void;
  /** Callback when inline edit ends  */
  onEnd?: (data?: ItemData) => void;
  /** Callback when the component is updated  */
  onUpdate?: (data?: ItemData) => void;
  /** Class name for styling */
  className?: string;
}

interface InlineEditCmpState {
  visible: boolean;
  id: string | null;
  value: string;
}

class InlineEditCmp extends React.Component<InlineEditProps, InlineEditCmpState> {
  el: HTMLTextAreaElement | null = null;
  debouncedKeyUpFn: null | ReturnType<typeof debounce> = null;
  style: {
    input?: React.CSSProperties;
    wrapper?: React.CSSProperties;
    placeholder?: React.CSSProperties;
  } = {};
  placeholder = '';

  static defaultProps = {
    property: 'label',
    shapeClassName: 'node-label',
    autoUpdate: false,
    inputProps: {},
    isCharacterCountShown: false,
    shouldBegin: () => true,
    shouldEnd: () => true,
    onBegin: noop,
    onEnd: noop,
    onUpdate: noop,
  };

  state = {
    visible: false,
    id: null,
    value: '',
  };

  componentDidMount() {
    const { graph } = this.props;

    // @ts-ignore
    graph?.on(EditorEvent.onInlineEditStateChange, this.handleInlineEditStateChange);
    graph?.on(GraphNodeEvent.onNodeDoubleClick, this.showInlineEdit);
  }

  componentWillUnmount() {
    const { graph } = this.props;

    graph?.off(EditorEvent.onInlineEditStateChange, this.handleInlineEditStateChange);
    graph?.off(GraphNodeEvent.onNodeDoubleClick, this.showInlineEdit);

    // If there is still a debounce event waiting, cancel it
    this.debouncedKeyUpFn?.cancel?.();
  }

  update = (item?: Item) => {
    const { autoUpdate, onUpdate = noop, property = '', executeCommand } = this.props;
    const { id, value } = this.state;

    if (!item || !id) return;

    if (autoUpdate) {
      executeCommand('update', {
        id,
        updateModel: { [property]: value },
        /**
         * If we begin to support TreeGraphs, this should be true
         * See line 66 of the following:
         * https://github.com/alibaba/GGEditor/blob/master/src/plugins/EditableLabel/index.tsx
         */
        forceRefreshLayout: false,
      });
    }

    onUpdate?.({ id, data: { [property]: value }, type: item?.getType() });
  };

  reset = () => {
    this.el = null;
    this.debouncedKeyUpFn?.cancel?.();
    this.setState({ visible: false, id: null, value: '' });
  };

  handleInlineEditStateChange = (e: InlineEditStateEvent) => {
    if (e.inlineEditState === InlineEditState.Show) {
      this.showInlineEdit(e);
    } else if (this.props.shouldEnd?.(e)) {
      this.hideInlineEdit();
    }
  };

  showInlineEdit = (e: Partial<InlineEditStateEvent> | Partial<GraphEvent>) => {
    const { property = '', onBegin = noop, shouldBegin = noop, graph } = this.props;
    const { item } = e;
    if (!graph || !item) return;
    const itemIsId = typeof item === 'string';
    const node = itemIsId ? graph.findById(item) : item;
    if (!node) return;
    if (!shouldBegin({ ...e, item: node }) || !isGraphEditable(graph)) return;
    this.updateItemView(node, true);
    this.setState(
      {
        visible: true,
        id: itemIsId ? item : node?.getID(),
        value: (node?.getModel()?.[property] || '') as string,
      },
      () => {
        const { el } = this;

        if (el) {
          el.focus();
          document.execCommand?.('selectAll', false, undefined);
          // Make sure that inline edit has the highest priority for rendering,
          // that the styles for it are definitely set
          graph.priorityState(item, ItemState.InlineEdit);
          onBegin(normalizeItemData(node));
        }
      },
    );
  };

  hideInlineEdit = () => {
    const { onEnd = noop } = this.props;
    const item = this.getItem();

    onEnd(normalizeItemData(item));

    this.reset();
    this.updateItemView(item, false);
  };

  getItem = () => {
    const { graph } = this.props;
    const { id } = this.state;
    return graph && id ? graph.findById(id) : null;
  };

  getPropertyShape = (item?: Item | null) => {
    const { shapeClassName } = this.props;
    if (!shapeClassName) return null;
    const group = (item || this.getItem())?.getContainer();
    return group?.findByClassName(shapeClassName) || null;
  };

  updateItemView = (item: Item | null, isEditing: boolean) => {
    const { graph } = this.props;
    if (!item || !graph) return;
    const shape = this.getPropertyShape(item);
    if (!shape) return;

    executeBatch(graph, () => {
      updateGlobal(graph, {
        plugin: {
          inlineEditInput: { state: isEditing ? VisibilityState.Show : VisibilityState.Hide },
        },
      });
      updateShapeVisibility(shape, !isEditing);
      item.setState(ItemState.Selected, isEditing);
      item.setState(ItemState.Active, isEditing);
      item.setState(ItemState.InlineEdit, isEditing);
      item.update({
        isInlineEditInProgress: isEditing,
        // setting to `null` instead of `false` so that internal states take priority
        isSelected: isEditing || null,
        isActive: isEditing || null,
      });
      this.style = isEditing ? getStyles({ graph, shape, item }) : {};
      this.placeholder = isEditing ? shape.get('attrs').placeholder || '' : '';
    });
  };

  runKeyUpFunction = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Do not run update on escape.  Just hide the textarea.
    if (['Escape'].includes(e.key)) {
      this.hideInlineEdit();
      // focus on the canvas element
      this.props.graph?.get('canvas').get('el')?.focus();
    }
  };

  handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const { shouldEnd, property = '' } = this.props;
    const { id, value } = this.state;
    if (!id) return;
    const item = this.getItem();
    if (!item || !shouldEnd?.({ ...e, item })) return;
    const prevText = item.getModel()[property];
    // If the value has changed, call any update functions
    if (prevText !== value) {
      this.update(item);
    }
    this.hideInlineEdit();
  };

  handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Stops event object reuse -- needed to get event key for a debounced event
    // https://reactjs.org/docs/legacy-event-pooling.html
    // https://medium.com/@anuhosad/debouncing-events-with-react-b8c405c33273
    e.persist();
    if (!this.debouncedKeyUpFn) {
      this.debouncedKeyUpFn = debounce(this.runKeyUpFunction, 300);
    }
    this.debouncedKeyUpFn(e);
  };

  render() {
    const { graph, inputProps = {}, isCharacterCountShown, className } = this.props;
    const { visible, value, id } = this.state;
    if (!graph || !visible || !id) return null;
    const item = this.getItem();
    if (!item) return null;
    const shape = this.getPropertyShape(item);
    if (!shape) return null;

    return ReactDOM.createPortal(
      <InlineEditWrapper
        style={this.style.wrapper}
        data-test={INLINE_EDIT_WRAPPER_TEST_ID}
        className={className}
      >
        <InlineEditInput
          data-test={INLINE_EDIT_INPUT_TEST_ID}
          ref={(el) => {
            this.el = el;
          }}
          value={value}
          style={this.style.input}
          onBlur={this.handleBlur}
          onChange={() => this.setState({ value: this.el?.value || '' })}
          onKeyUp={this.handleKeyUp}
          placeholder={this.placeholder}
          placeholderStyle={this.style.placeholder}
          {...inputProps}
        />
        {isCharacterCountShown && (
          <InlineEditCharacterCount data-test={INLINE_EDIT_CHARACTERCOUNT_TEST_ID}>
            {value.length}
            {inputProps.maxLength ? `/${inputProps.maxLength}` : null}
          </InlineEditCharacterCount>
        )}
      </InlineEditWrapper>,
      graph?.get('container'),
    );
  }
}

export const InlineEdit = withEditorContext<InlineEditProps>(InlineEditCmp);

export default InlineEdit;
